import React, { useCallback, useState } from 'react';
import { gamePlatforms, launchGame } from '../../lib/launchGame/utils';

import { GamesPlayInfo } from 'types/swagger';
import { LaunchGameType } from 'lib/launchGame/types';
import Script from 'next/script';
import { Wrapper } from '../wrapper';
import { callbackOnAuthenticated } from 'utility/auth';
import { capitalize, isMatch } from '../../utility/functions';
import { getNonce } from 'lib/policy';
import styles from './ActionBar.module.scss';
import { useCodeGameByWindowSize } from 'hooks/useCodeGameByWindowSize';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks/useInternalSession';
import useWindowSize from 'hooks/useWindowSize';

export type ActionBarProps = {
  title: string;
  titleHighlighted?: string;
  codicePiattaforma: number;
  isInstant: boolean;
  hasDemo: boolean;
  gameMode: string;
  playInfo: GamesPlayInfo;
  modalita?: string;
  provider?: string;
  idProduttore?: number;
  launchType?: string;
};

export const ActionBar = ({
  title,
  titleHighlighted,
  codicePiattaforma,
  playInfo,
  isInstant,
  hasDemo,
  modalita,
  provider,
  gameMode,
  idProduttore,
  launchType,
}: ActionBarProps) => {
  const [injectScript, setInjectScript] = useState<boolean>(false);
  const [labelGioca, labelDemo] = useGetLabelsByKeys(['gioca', 'demo']);
  const { session, isAuthenticated } = useInternalSession();
  const { isMobile } = useWindowSize();
  const [codeGame] = useCodeGameByWindowSize(playInfo?.codeGameDesktop, playInfo?.codeGameMobile);

  const gameScriptSrc = gamePlatforms[codicePiattaforma as keyof typeof gamePlatforms]?.urlParams?.FIM_CONF;

  const handleLaunchGame = useCallback(
    (isDemo: boolean) => {
      setInjectScript(true);
      launchGame({
        codicePiattaforma,
        cardNumber: session?.user?.cardNumber!,
        gameCode: codeGame,
        isDemo,
        isInstant,
        username: session?.user?.name!,
        provider: provider!,
        gameMode,
        modalita,
        contractCode: session?.user?.contractCode!,
        dimension: {
          height: playInfo.height,
          width: playInfo.width,
        },
        product: playInfo.product as LaunchGameType['product'],
        isMobile,
        idProvider: idProduttore,
        launchType,
      });
    },
    [
      codicePiattaforma,
      session?.user?.cardNumber,
      session?.user?.name,
      session?.user?.contractCode,
      codeGame,
      isInstant,
      provider,
      gameMode,
      modalita,
      playInfo.height,
      playInfo.width,
      playInfo.product,
      isMobile,
      idProduttore,
      launchType,
    ]
  );

  return (
    <React.Fragment>
      {gameScriptSrc && isMatch(gameScriptSrc, '^http') && injectScript && (
        <Script src={gameScriptSrc} nonce={getNonce()} />
      )}
      <Wrapper bgColorTransparent noPaddingRight noPaddingLeft noPaddingTop noPaddingBottom>
        <div className={styles.container}>
          <strong className={styles.title} suppressHydrationWarning>
            {title}{' '}
            {titleHighlighted && <span className={styles.titleHighlighted}>{capitalize(titleHighlighted)}</span>}
          </strong>
          <section className={styles.ctaContainer}>
            {hasDemo && (
              <button
                type="button"
                onClick={() => callbackOnAuthenticated(isAuthenticated, () => handleLaunchGame(true))}
                className={styles.demoCta}
              >
                {labelDemo}
              </button>
            )}
            {codicePiattaforma && (
              <button
                type="button"
                onClick={() => callbackOnAuthenticated(isAuthenticated, () => handleLaunchGame(false))}
                className={styles.playCta}
              >
                {labelGioca}
              </button>
            )}
          </section>
        </div>
      </Wrapper>
    </React.Fragment>
  );
};
